import React from 'react'
import { PaymentInterval, RecurlyPrice } from './Recurly'
import { UpgradeTideways6, UpgradeTideways6PlanChoice } from './UpgradeTideways6'
import { RequestHistory } from './RequestHistory'
import { TidewaysContext } from '../app/context'
import Router from '../router'

interface Props {
    recurlyPricing: any
    upgrade: UpgradeTideways6
    onPlanChange: (plan: string) => void
    onRequestsChange: (requests: number) => void
    selectedPlan: string
    interval: PaymentInterval
    currency: string
    tidewaysContext?: TidewaysContext
    router?: Router
}
interface StateProps {
    requestBracket: number
    recurlyPricing: {
        [key: string]: RecurlyPrice
    }
    selectedTierBelowEstimateWarning: boolean
}

const requests = [5_000_000, 15_000_000, 25_000_000, 50_000_000, 100_000_000, 150_000_000, 250_000_000, 500_000_000, 999_999_998]
const requestLabels = ['5M', '15M', '25M', '50M', '100M', '150M', '250M', '500M', '1B']
const tracesPerMinute = {
    tideways8_team: [5, 10, 15, 20, 30, 40, 50, 60, 70],
    tideways8_business: [25, 50, 75, 100, 125, 150, 175, 200, 250],
}
const triggerTraceCredits = {
    tideways8_team: [500, 1500, 2000, 2500, 3000, 4000, 5000, 6000, 7000],
    tideways8_business: [10_000, 15_000, 20_000, 25_000, 30_000, 35_000, 40_000, 45_000, 50_000],
}

class SelectTideways8Plans extends React.Component<Props, StateProps> {
    constructor(props: Props) {
        super(props)

        this.state = {
            recurlyPricing: {},
            requestBracket: Math.max(0, requests.indexOf(this.props.upgrade.startRequests)),
            selectedTierBelowEstimateWarning: false,
        }
    }
    public componentDidMount() {
        this.fetchAndCalculatePricing(this.state.requestBracket).then(recurlyPricing => {
            this.setState({ recurlyPricing })
        })
    }

    public componentDidUpdate(prevProps: Props, prevState: StateProps) {
        if (prevProps.interval !== this.props.interval || prevProps.currency !== this.props.currency) {
            this.fetchAndCalculatePricing(this.state.requestBracket).then(recurlyPricing => {
                this.setState({ recurlyPricing })
            })
        }
    }

    private async fetchAndCalculatePricing(requestBracket: number) {
        const prices: { [key: string]: RecurlyPrice } = {}

        for (const planCode of this.props.upgrade.availablePlans) {
            const promise = new Promise<RecurlyPrice>((resolve, reject) => {
                // @ts-ignore
                recurly
                    .Pricing()
                    .plan(planCode.name + (this.props.interval === PaymentInterval.Yearly ? '_1y' : ''))
                    .currency(this.props.currency)
                    .addon('requests', { quantity: requests[requestBracket] })
                    .done(pricing => {
                        resolve(pricing)
                    })
            })

            prices[planCode.name] = await promise
        }

        return prices
    }

    public onRequestBracketChange(ev) {
        let requestBracket = parseInt(ev.target.value)
        let selectedTierBelowEstimateWarning = false

        // do not allow picking a bracket that is 2+ levels below estimate
        if (requestBracket + 1 < requests.length - 1 && requests[requestBracket + 1] < this.props.upgrade.estimatedMonthlyRequests) {
            selectedTierBelowEstimateWarning = true
        }

        // prevent buying the lowest plan when you have more requests estimated than it covers.
        if (this.isLowestPlanButAlreadyShowedHigherRequestsDuringTrial(requestBracket)) {
            requestBracket = 1
        }

        this.fetchAndCalculatePricing(requestBracket).then(recurlyPricing => {
            this.setState({ requestBracket: requestBracket, selectedTierBelowEstimateWarning, recurlyPricing })
        })
        this.props.onRequestsChange(requests[requestBracket])
    }

    private isLowestPlanButAlreadyShowedHigherRequestsDuringTrial(requestBracket: number) {
        return this.props.upgrade.estimatedMonthlyRequests > requests[0] && requestBracket === 0
    }

    public render() {
        const paymentPeriodName = this.props.interval === PaymentInterval.Monthly ? 'month' : 'year'
        const { router, tidewaysContext } = this.props

        return (
            <>
                <div className="row">
                    <div className="col-lg-4">
                        <div className="box">
                            <div className="box-header">
                                <h3 className="box-title">
                                    Expected Monthly Requests
                                    {router && tidewaysContext?.organization?.name ? (
                                        <RequestHistory router={router} organization={tidewaysContext?.organization.name} />
                                    ) : null}
                                </h3>
                            </div>
                            <div className="box-body box-body-larger" style={{ minHeight: '380px' }}>
                                <input
                                    type="range"
                                    min="0"
                                    max={requests.length - 1}
                                    onInput={ev => this.onRequestBracketChange(ev)}
                                    value={this.state.requestBracket}
                                />
                                <div>
                                    <big>{requestLabels[this.state.requestBracket]} requests per month</big>
                                </div>
                                <p style={{ marginTop: '10px' }}>
                                    Based on your current usage, we estimate around{' '}
                                    <strong>{Math.ceil(this.props.upgrade.estimatedMonthlyRequests / 1000000)}</strong> million requests per
                                    month in this account.
                                </p>

                                {this.state.selectedTierBelowEstimateWarning ? (
                                    <p className="alert alert-warning">
                                        Warning: You selected a tier that is below your currently estimated monthly requests. This will
                                        usually lead to a limit reached notification and trigger the two month auto-upgrade process, unless
                                        you stop collecting data from some projects, servers, or use sampling.
                                    </p>
                                ) : null}

                                <div
                                    className="alert alert-info hidden-xs hidden-sm hidden-md"
                                    style={{ marginTop: '20px', marginLeft: '0px', marginBottom: '0px' }}
                                >
                                    <p>Every web request, API call, run background script or job counts as request.</p>

                                    <p>80% of our customers are in the range up to 50 million requests per month.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-8">
                        <div className="row">
                            {this.props.upgrade.availablePlans.map(plan => {
                                if (plan.name === 'tideways8_enterprise') {
                                    return null
                                }

                                if (!this.state.recurlyPricing[plan.name]) {
                                    return null
                                }

                                const price = this.state.recurlyPricing[plan.name]

                                return this.renderPlan(plan, price, paymentPeriodName)
                            })}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="box">
                            <div className="box-body box-body-larger">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <strong>Enterprise</strong> Higher Limits, Azure SSO, Custom Terms and Personalized Onboarding
                                    </div>
                                    <div className="col-lg-4 text-right">
                                        <a href="mailto:customer-success@tideways.com">Contact Customer Success</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    private renderPlan(plan: UpgradeTideways6PlanChoice, price: RecurlyPrice, paymentPeriodName: string) {
        return (
            <div key={plan.name} className="col-lg-6">
                <div className="box">
                    <div className="box-header">
                        <h2 className="box-title">{plan.label}</h2>
                    </div>
                    <div className="box-body box-body-larger" style={{ minHeight: '380px' }}>
                        <div className="centered">
                            <strong>
                                <big>
                                    {price.now.subtotal} {price.currency.symbol}
                                </big>
                            </strong>
                            <small> / {paymentPeriodName}</small>
                        </div>
                        <div className="centered" style={{ height: '50px' }}>
                            {plan.name === this.props.selectedPlan && <p className="pt-8 label label-success">Selected Plan</p>}

                            {this.getPlanRank(plan.name) > this.getPlanRank(this.props.selectedPlan) && (
                                <button
                                    disabled={plan.disabled}
                                    className="btn btn-primary"
                                    onClick={() => this.props.onPlanChange(plan.name)}
                                >
                                    Upgrade to {plan.label}
                                </button>
                            )}

                            {this.getPlanRank(plan.name) < this.getPlanRank(this.props.selectedPlan) && (
                                <button
                                    disabled={plan.disabled}
                                    className="btn btn-default"
                                    onClick={() => this.props.onPlanChange(plan.name)}
                                >
                                    Downgrade to {plan.label}
                                </button>
                            )}
                        </div>
                        <ul style={{ paddingLeft: '20px' }}>
                            <li>Up to {requestLabels[this.state.requestBracket]} monthly requests</li>
                            <li>{tracesPerMinute[plan.name][this.state.requestBracket]} traces per minute</li>
                            <li>Max {plan.name === 'tideways8_team' ? '500' : '1500'} spans per trace</li>
                            <li>{triggerTraceCredits[plan.name][this.state.requestBracket]} monthly trigger trace credits</li>
                            <li>{plan.retentionDays} days of data retention</li>
                            <li>Unlimited projects, services, users</li>
                            <li>Basic {plan.advancedIntegrations && <span>&amp; Advanced</span>} Integrations</li>
                            <li>GitHub {plan.premium && <span>&amp; Azure AD</span>} SSO</li>
                            {plan.advancedIntegrations && <li>Distributed Profiling and Traces in History</li>}
                            {plan.includesTeamBasedAccess && <li>Team-based ACLs and Notifications</li>}
                        </ul>
                    </div>
                </div>
            </div>
        )
    }

    private getPlanRank(plan: string) {
        if (plan.includes('team')) {
            return 1
        } else if (plan.includes('business')) {
            return 2
        } else if (plan.includes('enterprise')) {
            return 3
        }
        return 0
    }
}

export default SelectTideways8Plans
